import { Close } from '@ahamove/icons';
import {
  type ButtonProps,
  Drawer,
  Modal,
  rem,
  Skeleton,
  Stack,
  Box,
  type DrawerStylesNames,
  type ModalStylesNames,
} from '@mantine/core';
import React, {
  type CSSProperties,
  type FC,
  type ReactNode,
  useMemo,
  memo,
  Suspense,
} from 'react';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';

// ==================== TYPES ====================
type ModalStyles = Partial<Record<ModalStylesNames, CSSProperties>>;
type DrawerStyles = Partial<Record<DrawerStylesNames, CSSProperties>>;

interface ModalWrapperProps {
  title?: string;
  modalId?: string;
  children: ReactNode;
  isOpen: boolean;
  onClose: () => void;
  optionStyle?: {
    titleStyle?: CSSProperties;
    bodyStyle?: CSSProperties;
    headerStyle?: CSSProperties;
    contentStyle?: CSSProperties;
  };
  closeButtonProps?: ButtonProps;
  closeOnClickOutside?: boolean;
  closeOnEscape?: boolean;
  withSkeleton?: boolean;
  skeletonConfig?: {
    rows?: number;
    height?: number;
    spacing?: number;
  };
}

// ==================== CONSTANTS ====================
const DEFAULT_DRAWER_CLOSE_STYLE = {
  '--cb-icon-size': rem(24),
} as CSSProperties;

const DEFAULT_DRAWER_TITLE_STYLE = {
  flex: 1,
  textAlign: 'center' as const,
  fontSize: 18,
  lineHeight: '24px',
  fontWeight: 700,
} as const;

const DEFAULT_DRAWER_HEADER_STYLE = {
  paddingTop: rem(12),
  paddingBottom: rem(12),
  borderBottom: '1px solid var(--mantine-color-neutral-0)',
  minHeight: rem(56),
} as const;

const DEFAULT_DRAWER_BODY_STYLE = {
  height: 'fit-content' as const,
  position: 'relative' as const,
  backgroundColor: 'white',
  padding: rem(16),
} as const;

const DEFAULT_DRAWER_CONTENT_STYLE = {
  maxHeight: '95svh',
  height: 'fit-content' as const,
  borderTopLeftRadius: rem(14),
  borderTopRightRadius: rem(14),
} as const;

const DEFAULT_MODAL_TITLE_STYLE = {
  fontSize: 20,
  fontWeight: 700,
} as const;

const DEFAULT_MODAL_HEADER_STYLE = {
  height: rem(56),
  backgroundColor: 'white',
  paddingLeft: rem(24),
  paddingRight: rem(24),
} as const;

const DEFAULT_MODAL_CONTENT_STYLE = {
  height: 'fit-content' as const,
  backgroundColor: 'var(--mantine-color-neutral-0)',
} as const;

const DEFAULT_MODAL_BODY_STYLE = {
  height: 'fit-content' as const,
  position: 'relative' as const,
  backgroundColor: 'white',
  paddingLeft: rem(24),
  paddingRight: rem(24),
  paddingBottom: rem(24),
} as const;

const DEFAULT_SKELETON_CONFIG = {
  rows: 3,
  height: 60,
  spacing: 16,
} as const;

// ==================== SKELETON COMPONENTS ====================
interface SkeletonProps {
  isMobile?: boolean;
  config?: {
    rows?: number;
    height?: number;
    spacing?: number;
  };
}

const ModalHeaderSkeleton: FC<{ isMobile?: boolean }> = memo(({ isMobile }) => (
  <Box
    style={{
      padding: isMobile ? rem(12) : rem(24),
      borderBottom: '1px solid var(--mantine-color-neutral-0)',
      backgroundColor: 'white',
    }}
  >
    <Skeleton height={24} width="60%" radius="md" />
  </Box>
));

ModalHeaderSkeleton.displayName = 'ModalHeaderSkeleton';

const ModalBodySkeleton: FC<SkeletonProps> = memo(
  ({ isMobile, config = DEFAULT_SKELETON_CONFIG }) => {
    const { rows, height, spacing } = { ...DEFAULT_SKELETON_CONFIG, ...config };

    return (
      <Box
        style={{
          padding: isMobile ? rem(16) : rem(24),
          backgroundColor: 'white',
        }}
      >
        <Stack gap={spacing}>
          {/* Main content skeleton */}
          {Array.from({ length: rows }).map((_, index) => (
            <Box key={index}>
              <Skeleton height={height} radius="md" mb={8} />
              {/* Sub items skeleton */}
              {index === 0 && (
                <Stack gap={8} mt={12}>
                  <Skeleton height={40} radius="md" />
                  <Skeleton height={40} radius="md" />
                </Stack>
              )}
            </Box>
          ))}

          {/* Buttons skeleton */}
          <Box
            style={{
              display: 'flex',
              gap: rem(12),
              marginTop: rem(24),
            }}
          >
            <Skeleton height={48} radius="md" style={{ flex: 1 }} />
            <Skeleton height={48} radius="md" style={{ flex: 1 }} />
          </Box>

          {/* Additional info skeleton */}
          <Box mt={16}>
            <Skeleton height={20} width="40%" radius="md" mb={8} />
            <Skeleton height={16} width="70%" radius="md" />
          </Box>
        </Stack>
      </Box>
    );
  }
);

ModalBodySkeleton.displayName = 'ModalBodySkeleton';

const ModalSkeleton: FC<SkeletonProps> = memo(({ isMobile, config }) => (
  <Box
    style={{
      backgroundColor: 'white',
      borderRadius: isMobile ? `${rem(14)} ${rem(14)} 0 0` : rem(8),
      overflow: 'hidden',
      minHeight: isMobile ? '50vh' : rem(400),
    }}
  >
    <ModalHeaderSkeleton isMobile={isMobile} />
    <ModalBodySkeleton isMobile={isMobile} config={config} />
  </Box>
));

ModalSkeleton.displayName = 'ModalSkeleton';

// ==================== DRAWER COMPONENT ====================
interface DrawerWrapperProps {
  title?: string;
  isOpen: boolean;
  onClose: () => void;
  closeButtonProps: ButtonProps;
  closeOnEscape: boolean;
  closeOnClickOutside: boolean;
  styles: DrawerStyles;
  children: ReactNode;
}

const DrawerWrapper: FC<DrawerWrapperProps> = memo(
  ({
    title,
    isOpen,
    onClose,
    closeButtonProps,
    closeOnEscape,
    closeOnClickOutside,
    styles,
    children,
  }) => (
    <Drawer
      zIndex={999}
      opened={isOpen}
      onClose={onClose}
      position="bottom"
      title={title}
      closeButtonProps={closeButtonProps}
      closeOnEscape={closeOnEscape}
      closeOnClickOutside={closeOnClickOutside}
      styles={styles}
    >
      {children}
    </Drawer>
  )
);

DrawerWrapper.displayName = 'DrawerWrapper';

// ==================== MODAL COMPONENT ====================
interface ModalContainerProps {
  modalId?: string;
  title?: string;
  isOpen: boolean;
  onClose: () => void;
  closeButtonProps: ButtonProps;
  closeOnEscape: boolean;
  closeOnClickOutside: boolean;
  styles: ModalStyles;
  children: ReactNode;
}

const ModalContainer: FC<ModalContainerProps> = memo(
  ({
    modalId,
    title,
    isOpen,
    onClose,
    closeButtonProps,
    closeOnEscape,
    closeOnClickOutside,
    styles,
    children,
  }) => (
    <Modal
      id={modalId}
      closeOnClickOutside={closeOnClickOutside}
      closeOnEscape={closeOnEscape}
      centered
      opened={isOpen}
      onClose={onClose}
      title={title}
      closeButtonProps={closeButtonProps}
      styles={styles}
    >
      {children}
    </Modal>
  )
);

ModalContainer.displayName = 'ModalContainer';

// ==================== MAIN COMPONENT ====================
const ModalWrapper: FC<ModalWrapperProps> = memo(
  ({
    title,
    children,
    isOpen,
    modalId,
    onClose,
    optionStyle = {},
    closeButtonProps = {},
    closeOnClickOutside = false,
    closeOnEscape = false,
    withSkeleton = false,
    skeletonConfig,
  }) => {
    const { titleStyle, bodyStyle, headerStyle, contentStyle } = optionStyle;
    const isMobile = useIsMobile();

    // ========== Memoized Styles ==========
    const drawerStyles = useMemo<DrawerStyles>(
      () => ({
        close: DEFAULT_DRAWER_CLOSE_STYLE,
        title: { ...DEFAULT_DRAWER_TITLE_STYLE, ...titleStyle },
        header: { ...DEFAULT_DRAWER_HEADER_STYLE, ...headerStyle },
        body: { ...DEFAULT_DRAWER_BODY_STYLE, ...bodyStyle },
        content: { ...DEFAULT_DRAWER_CONTENT_STYLE, ...contentStyle },
      }),
      [titleStyle, bodyStyle, headerStyle, contentStyle]
    );

    const modalStyles = useMemo<ModalStyles>(
      () => ({
        title: { ...DEFAULT_MODAL_TITLE_STYLE, ...titleStyle },
        header: { ...DEFAULT_MODAL_HEADER_STYLE, ...headerStyle },
        content: { ...DEFAULT_MODAL_CONTENT_STYLE, ...contentStyle },
        body: { ...DEFAULT_MODAL_BODY_STYLE, ...bodyStyle },
      }),
      [titleStyle, bodyStyle, headerStyle, contentStyle]
    );

    // ========== Memoized Props ==========
    const drawerCloseButtonProps = useMemo(
      () => ({
        pos: 'absolute' as const,
        left: rem(12),
        ...closeButtonProps,
      }),
      [closeButtonProps]
    );

    const modalCloseButtonProps = useMemo(
      () => ({
        ...closeButtonProps,
        mr: rem(-8),
        radius: 'xl' as const,
        icon: <Close width={22} height={22} />,
      }),
      [closeButtonProps]
    );

    // ========== Skeleton Component ==========
    const skeletonElement = useMemo(
      () => <ModalSkeleton isMobile={isMobile} config={skeletonConfig} />,
      [isMobile, skeletonConfig]
    );

    // ========== Content Wrapper ==========
    const wrappedChildren = useMemo(() => {
      if (withSkeleton) {
        return <Suspense fallback={skeletonElement}>{children}</Suspense>;
      }
      return children;
    }, [withSkeleton, children, skeletonElement]);

    // ========== Render ==========
    if (isMobile) {
      return (
        <DrawerWrapper
          title={title}
          isOpen={isOpen}
          onClose={onClose}
          closeButtonProps={drawerCloseButtonProps}
          closeOnEscape={closeOnEscape}
          closeOnClickOutside={closeOnClickOutside}
          styles={drawerStyles}
        >
          {wrappedChildren}
        </DrawerWrapper>
      );
    }

    return (
      <ModalContainer
        modalId={modalId}
        title={title}
        isOpen={isOpen}
        onClose={onClose}
        closeButtonProps={modalCloseButtonProps}
        closeOnEscape={closeOnEscape}
        closeOnClickOutside={closeOnClickOutside}
        styles={modalStyles}
      >
        {wrappedChildren}
      </ModalContainer>
    );
  }
);

ModalWrapper.displayName = 'ModalWrapper';

// ==================== EXPORTS ====================
export default ModalWrapper;
export type { ModalWrapperProps, ModalStyles, DrawerStyles };
